import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible'

import {UserInteractionProvider} from '../../hooks/use-user-interaction'
import CommerceAPI from '../../commerce-api'
import {
    BasketProvider,
    CommerceAPIProvider,
    CustomerProductListsProvider,
    CustomerProvider
} from '../../commerce-api/contexts'
import {GoogleMapsAPIProvider} from '../../google-maps-api'
import GoogleTranslateAPI from '../../google-translate-api'
import {GoogleTranslateAPIProvider} from '../../google-translate-api/contexts'
import CuralateAPI from '../../curalate-api'
import {CuralateAPIProvider} from '../../curalate-api/contexts'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {GtmConfigProvider} from '../../gtm-api/contexts'
import BazaarvoiceAPI from '../../bazaarvoice-api'
import {BazaarvoiceAPIProvider} from '../../bazaarvoice-api/context'
import {CroProvider} from '../cro'
import {compileTemplate} from '../../utils/text-utils'
import {localeToBVLocale} from '../../bazaarvoice-api/utils'
import useOcapiSite from '../../hooks/useOcapiSite'
import useMultiSite from '../../hooks/use-multi-site'
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext'
import {PayPalScriptProvider} from '@paypal/react-paypal-js'
import {KlarnaProvider} from '../klarna-pay-later/context'
import {BabylistAPIProvider} from '../../babylist-api'
import {HydrationProvider} from '../../contexts/hydration'
import {MediaQueriesProvider} from '../../contexts/media-queries'

const LegacyProvidersWithOcapiSite = ({children}) => {
    const {app: appConfig} = getConfig()
    const {locale} = useMultiSite()
    const ocapiSite = useOcapiSite()
    const ocapiSitePreferences = ocapiSite.preferences
    const {
        bvApiEnv = 'stg',
        bvApiKey,
        bvApiScriptUrlTemplate,
        curalateDataSourceId,
        googleMapsApiKey,
        googleTranslateEnabled,
        googleTranslateApiKey,
        paypalLaterClientID,
        klarnaLaterEnabled,
        klarnaLaterClientID,
        klarnaLaterEnvironment
    } = ocapiSitePreferences

    const googleTranslateApi = useMemo(
        () =>
            new GoogleTranslateAPI({
                ...appConfig.googleTranslateAPI,
                apiKey: googleTranslateApiKey,
                apiEnabled: googleTranslateEnabled,
                locale
            }),
        [appConfig, locale, googleTranslateApiKey, googleTranslateEnabled]
    )

    const googleMapsApi = useMemo(() => {
        return {...appConfig.googleMapsAPI, apiKey: googleMapsApiKey, locale: locale.id}
    }, [appConfig.googleMapsAPI, googleMapsApiKey, locale])

    const curalateApi = useMemo(
        () =>
            new CuralateAPI({...appConfig.curalateAPI, dataSourceId: curalateDataSourceId, locale}),
        [appConfig, locale, curalateDataSourceId]
    )
    const bazaarvoiceApi = useMemo(
        () =>
            new BazaarvoiceAPI({
                ...appConfig.bazaarvoiceAPI,
                ...appConfig.bazaarvoiceAPI?.proxies?.[bvApiEnv],
                bvApiKey,
                bvApiEnv,
                bvApiScriptUrl: compileTemplate(bvApiScriptUrlTemplate, {
                    locale: localeToBVLocale(locale)
                }),
                locale
            }),
        [appConfig, locale, bvApiEnv, bvApiKey, bvApiScriptUrlTemplate]
    )

    return (
        <GoogleTranslateAPIProvider value={googleTranslateApi}>
            <GoogleMapsAPIProvider config={googleMapsApi}>
                <CuralateAPIProvider value={curalateApi}>
                    <BazaarvoiceAPIProvider value={bazaarvoiceApi}>
                        <GtmConfigProvider value={appConfig.gtmAPI}>
                            <BabylistAPIProvider
                                enabled={ocapiSitePreferences.babylistEnabled}
                                siteKey={ocapiSitePreferences.babylistSiteKey}
                            >
                                <PayPalScriptProvider
                                    // defer loading of paypal script until needed
                                    // see https://paypal.github.io/react-paypal-js/?path=/story/example-paypalscriptprovider--default&args=deferLoading:true
                                    deferLoading
                                    options={{
                                        clientId: paypalLaterClientID,
                                        // paypal pay later requires buttons and messages components
                                        // see https://developer.paypal.com/docs/checkout/pay-later/it/integrate/#enable-pay-later-messaging-on-your-website
                                        components: 'messages,buttons',
                                        // found in https://github.com/paypal/paypal-js/blob/main/packages/react-paypal-js/src/constants.ts#L12C22-L12C35
                                        dataNamespace: 'stokkePaypalPayLater'
                                    }}
                                >
                                    <KlarnaProvider
                                        enabled={klarnaLaterEnabled}
                                        clientId={klarnaLaterClientID}
                                        environment={klarnaLaterEnvironment}
                                    >
                                        {children}
                                    </KlarnaProvider>
                                </PayPalScriptProvider>
                            </BabylistAPIProvider>
                        </GtmConfigProvider>
                    </BazaarvoiceAPIProvider>
                </CuralateAPIProvider>
            </GoogleMapsAPIProvider>
        </GoogleTranslateAPIProvider>
    )
}

LegacyProvidersWithOcapiSite.propTypes = {
    children: PropTypes.node
}

const LegacyProviders = ({
    children,
    basket: initialBasket = null,
    customer: initialCustomer = null
}) => {
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const currency = locale.preferredCurrency
    const auth = useAuthContext()

    const [basket, setBasket] = useState(initialBasket)
    const [customer, setCustomer] = useState(initialCustomer)

    const commerceAPI = useMemo(
        () =>
            new CommerceAPI(
                {
                    ...appConfig.commerceAPI,
                    parameters: {
                        ...appConfig.commerceAPI.parameters,
                        siteId: site.id
                    },
                    einsteinConfig: {
                        ...appConfig.einsteinAPI,
                        siteId: `${appConfig.einsteinAPI.siteId.split('-')[0]}-${site.id}`
                    },
                    sapiConfig: appConfig.storefrontAPI,
                    locale: locale.id,
                    currency
                },
                auth
            ),
        [auth, appConfig, locale.id, currency, site.id]
    )

    return (
        <CommerceAPIProvider value={commerceAPI}>
            <CustomerProvider value={{customer, setCustomer}}>
                <BasketProvider value={{basket, setBasket}}>
                    <LegacyProvidersWithOcapiSite>
                        <CroProvider>
                            <CustomerProductListsProvider>
                                <HydrationProvider>
                                    <MediaQueriesProvider>
                                        <UserInteractionProvider>
                                            {children}
                                        </UserInteractionProvider>
                                    </MediaQueriesProvider>
                                </HydrationProvider>
                            </CustomerProductListsProvider>
                        </CroProvider>
                    </LegacyProvidersWithOcapiSite>
                </BasketProvider>
            </CustomerProvider>
        </CommerceAPIProvider>
    )
}

LegacyProviders.propTypes = {
    children: PropTypes.node,
    basket: PropTypes.object,
    customer: PropTypes.object
}

export default LegacyProviders
